import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VehicleAPI, CustomerAPI, extractError } from "@/lib/api";
import { RegistrationInput } from "@/components/RegistrationInput";
import { RegistryLookupButton } from "@/components/RegistryLookupButton";
import { CustomerCombobox } from "@/components/CustomerCombobox";
import { FUEL_TYPES, TRANSMISSIONS } from "@/lib/india";
import { toast } from "sonner";

const NOW = new Date().getFullYear();

export default function VehicleNewPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const initialCustomerId = params.get("customer_id") || "";
    const [customer, setCustomer] = useState(null);

    const [form, setForm] = useState({
        customer_id: initialCustomerId,
        registration_number: "",
        make: "",
        model: "",
        variant: "",
        year: NOW - 1,
        fuel_type: "petrol",
        transmission: "manual",
        vin: "",
        engine_number: "",
        chassis_number: "",
        color: "",
        odometer_current: 0,
        odometer_unit: "km",
        registry_lookup_data: null,
    });

    useEffect(() => {
        if (initialCustomerId) {
            CustomerAPI.get(initialCustomerId).then(setCustomer).catch(() => {});
        }
    }, [initialCustomerId]);

    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    function onCustomerPicked(id, c) {
        set("customer_id", id);
        setCustomer(c);
    }

    function applyLookup(data) {
        if (!data) return;
        setForm((f) => ({
            ...f,
            make: data.make || f.make,
            model: data.model || f.model,
            variant: data.variant || f.variant,
            year: data.year || f.year,
            fuel_type: data.fuel_type || f.fuel_type,
            engine_number: data.engine_number || f.engine_number,
            chassis_number: data.chassis_number || f.chassis_number,
            registry_lookup_data: data,
        }));
    }

    async function save() {
        setBusy(true);
        try {
            const body = { ...form };
            if (!body.vin) delete body.vin;
            if (!body.variant) delete body.variant;
            if (!body.engine_number) delete body.engine_number;
            if (!body.chassis_number) delete body.chassis_number;
            if (!body.color) delete body.color;
            body.year = Number(body.year);
            body.odometer_current = Number(body.odometer_current || 0);
            const v = await VehicleAPI.create(body);
            toast.success(`Vehicle ${v.registration_number} added`);
            navigate(`/vehicles/${v.id}`);
        } catch (e) {
            toast.error(extractError(e));
        } finally { setBusy(false); }
    }

    return (
        <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="vehicle-new-page">
            <header className="flex items-center justify-between">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">New vehicle</h1>
                    <p className="mt-1 text-sm text-zinc-500">
                        {customer ? <>Adding under <Link to={`/customers/${customer.id}`} className="text-brand-redDeep hover:underline">{customer.display_name}</Link></> : "Pick the customer first, then enter the vehicle details."}
                    </p>
                </div>
                <Link to="/vehicles" className="text-sm text-zinc-500 hover:underline">← Back</Link>
            </header>

            <Card className="p-6">
                <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Customer</h2>
                <Field label="Owner" required>
                    <CustomerCombobox
                        value={form.customer_id}
                        onChange={onCustomerPicked}
                        testid="customer-combobox"
                    />
                </Field>
                <p className="mt-2 text-xs text-zinc-500">
                    Don't see the customer? <Link to="/customers/new" className="text-brand-redDeep hover:underline">Add a new customer</Link> first.
                </p>
            </Card>

            <Card className="p-6">
                <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Identification</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field label="Registration number" required>
                        <div className="flex gap-2">
                            <div className="flex-1"><RegistrationInput value={form.registration_number} onChange={(v) => set("registration_number", v)} testid="vehicle-reg" /></div>
                            <RegistryLookupButton registration={form.registration_number} onResult={applyLookup} />
                        </div>
                    </Field>
                    <Field label="VIN (optional)">
                        <Input value={form.vin} onChange={(e) => set("vin", e.target.value.toUpperCase())} className="font-mono uppercase" maxLength={17} data-testid="vehicle-vin" />
                    </Field>
                    <Field label="Make" required><Input value={form.make} onChange={(e) => set("make", e.target.value)} data-testid="vehicle-make" /></Field>
                    <Field label="Model" required><Input value={form.model} onChange={(e) => set("model", e.target.value)} data-testid="vehicle-model" /></Field>
                    <Field label="Variant"><Input value={form.variant} onChange={(e) => set("variant", e.target.value)} /></Field>
                    <Field label="Year" required>
                        <Input type="number" min={1980} max={NOW + 1} value={form.year} onChange={(e) => set("year", e.target.value)} className="font-mono tnum" data-testid="vehicle-year" />
                    </Field>
                    <Field label="Fuel">
                        <select value={form.fuel_type} onChange={(e) => set("fuel_type", e.target.value)}
                            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="vehicle-fuel">
                            {FUEL_TYPES.map((f) => <option key={f} value={f}>{f}</option>)}
                        </select>
                    </Field>
                    <Field label="Transmission">
                        <select value={form.transmission} onChange={(e) => set("transmission", e.target.value)}
                            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                            {TRANSMISSIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </Field>
                    <Field label="Color"><Input value={form.color} onChange={(e) => set("color", e.target.value)} /></Field>
                    <Field label="Odometer (km)">
                        <Input type="number" min={0} value={form.odometer_current} onChange={(e) => set("odometer_current", e.target.value)} className="font-mono tnum" data-testid="vehicle-odo" />
                    </Field>
                </div>
            </Card>

            <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" onClick={() => navigate(-1)}>Cancel</Button>
                <Button onClick={save} disabled={busy || !form.customer_id || !form.registration_number || !form.make || !form.model} className="bg-brand-red text-white hover:bg-brand-redDeep" data-testid="vehicle-save">
                    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save vehicle
                </Button>
            </div>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                {label}{required && <span className="ml-0.5 text-brand-red">*</span>}
            </Label>
            {children}
        </div>
    );
}
